import { Configuration, LogLevel } from '@azure/msal-browser'

// Partner Center app ID - used for Partner Center API access
const clientId = import.meta.env.VITE_AZURE_CLIENT_ID || ''
const partnerTenantId = import.meta.env.VITE_AZURE_TENANT_ID || ''

// Customer Graph app ID - used for Microsoft Graph API access in customer tenants
// This is a separate app that only has Graph permissions (no Partner Center API)
// to avoid consent issues when authorizing in customer tenants
const customerGraphClientId = import.meta.env.VITE_CUSTOMER_GRAPH_CLIENT_ID || ''

export const msalConfig: Configuration = {
  auth: {
    clientId,
    // Use partner tenant as default authority
    authority: `https://login.microsoftonline.com/${partnerTenantId}`,
    redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin,
    // Allow navigating to different tenant authorities for customer access
    knownAuthorities: ['login.microsoftonline.com'],
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message)
            return
          case LogLevel.Info:
            console.info(message)
            return
          case LogLevel.Verbose:
            console.debug(message)
            return
          case LogLevel.Warning:
            console.warn(message)
            return
        }
      },
    },
  },
}

export const loginRequest = {
  scopes: ['User.Read'],
}

// Request token for API calls - use Partner Center API scope for license data
export const apiRequest = {
  scopes: ['https://api.partnercenter.microsoft.com/user_impersonation'],
}

// Request token for M365 user management in a specific customer tenant
// Uses Microsoft Graph scopes for user/license management and sign-in activity
export const getCustomerGraphRequest = (customerTenantId: string) => ({
  scopes: [
    'https://graph.microsoft.com/User.ReadWrite.All', // Required for user updates and license assignment
    'https://graph.microsoft.com/Directory.Read.All',
    'https://graph.microsoft.com/AuditLog.Read.All',
    'https://graph.microsoft.com/UserAuthenticationMethod.ReadWrite.All', // Required for password reset
    'https://graph.microsoft.com/Reports.Read.All', // Required for mailbox usage reports
    'https://graph.microsoft.com/Files.Read.All', // Required for OneDrive info
  ],
  authority: `https://login.microsoftonline.com/${customerTenantId}`,
})

// Generic Graph request for partner tenant
export const graphApiRequest = {
  scopes: ['https://graph.microsoft.com/.default'],
}

// MSAL config for Customer Graph app (used for customer tenant Graph API access)
// This uses a separate app registration that only has Microsoft Graph permissions
export const customerGraphMsalConfig: Configuration = {
  auth: {
    clientId: customerGraphClientId,
    // Use common authority for multi-tenant access
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin,
    knownAuthorities: ['login.microsoftonline.com'],
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            console.error('[CustomerGraph]', message)
            return
          case LogLevel.Info:
            console.info('[CustomerGraph]', message)
            return
          case LogLevel.Verbose:
            console.debug('[CustomerGraph]', message)
            return
          case LogLevel.Warning:
            console.warn('[CustomerGraph]', message)
            return
        }
      },
    },
  },
}

// Export the customer graph client ID for use in consent URLs
export const getCustomerGraphClientId = () => customerGraphClientId
